import type { RepositoryIntelligenceEvidence } from '@ashniva/types';
import { Badge, Button } from '@ashniva/ui';

import { linesLabel } from '../ri-text';
import { useWorkspace } from '../workspace-context';

/** What a flag on an evidence item means, in plain words; anything else is shown as its code. */
const FLAGS: Record<string, string> = {
  instruction: 'Contains text that reads like an instruction to an AI; treated as code only',
  role_marker: 'Contains text that looks like a prompt marker; treated as code only',
  prompt_request: 'Contains text asking for prompts or secrets; treated as code only',
  citation_lookalike: 'Contains something that looks like a citation',
  fence_lookalike: 'Contains something that looks like an evidence boundary',
  link_request: 'Contains a request to add a link; no link is ever added',
  token_flood: 'Contains heavily repeated text',
};

/** Own keys only: a flag is Repository Intelligence's text, and `constructor` is a well-formed one. */
function flagText(flag: string): string {
  return (Object.hasOwn(FLAGS, flag) ? FLAGS[flag] : undefined) ?? `Flagged: ${flag}`;
}

/** The evidence an answer was put together from (P14-06): each item's place in the code and its excerpt. */
export function EvidencePanel({ evidence }: { evidence: RepositoryIntelligenceEvidence[] }) {
  const { openCode } = useWorkspace();
  if (evidence.length === 0) {
    return null;
  }
  return (
    <section aria-label="Evidence" className="ri-stack">
      <h4>Evidence</h4>
      <ol className="ri-list">
        {evidence.map((item) => (
          <li key={item.id} className="ri-list__item">
            <div className="ri-row">
              <Badge tone="neutral">{item.id}</Badge>
              <span className="ri-mono">{item.path}</span>
              <span className="ri-muted">{linesLabel(item.startLine, item.endLine)}</span>
              {item.symbol ? <span className="ri-mono">{item.symbol}</span> : null}
              <span className="ri-muted">{item.role.replace(/_/g, ' ')}</span>
            </div>
            {item.flags.length > 0 ? (
              <ul className="ri-list">
                {item.flags.map((flag) => (
                  <li key={flag} className="ri-muted">
                    <span aria-hidden="true">! </span>
                    {flagText(flag)}
                  </li>
                ))}
              </ul>
            ) : null}
            <pre className="ri-snippet">{item.excerpt}</pre>
            {item.excerptTruncated ? <p className="ri-muted">The excerpt is shortened.</p> : null}
            <div>
              <Button
                size="sm"
                aria-label={`Open ${item.id}: ${item.path}, ${linesLabel(item.startLine, item.endLine)}`}
                onClick={() =>
                  openCode({ path: item.path, start: item.startLine, end: item.endLine })
                }
              >
                Open
              </Button>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
